/**
 * Currency Input Component
 * Formats input as currency ($XXX.XX)
 * Matches Angular's appAmount directive behavior
 */

import { useState, useEffect } from 'react'
import { cn } from '@lib/utils/helpers'

/** Standard max digits for income (before decimal) to avoid overflow – e.g. 15 = up to 999,999,999,999,999.99 */
const DEFAULT_MAX_DIGITS_BEFORE_DECIMAL = 15

const PREFIX = '$ '

interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string
  onChange: (value: string) => void
  error?: string
  /** Max digits before decimal (default 15). Prevents >20 digit entry that causes errors. */
  maxDigitsBeforeDecimal?: number
  /** When true, `$ ` prefix cannot be removed; empty blur shows `$ 0.00`. */
  lockPrefix?: boolean
}

function formatFocusedDisplay(cleaned: string, lockPrefix: boolean): string {
  if (!cleaned) return ''
  const parts = cleaned.split('.')
  const formatted =
    parts.length === 2 ? `${parts[0]}.${parts[1].slice(0, 2)}` : cleaned
  return lockPrefix ? formatted : `${PREFIX}${formatted}`
}

function formatBlurredDisplay(cleaned: string, lockPrefix: boolean): string {
  if (!cleaned) return lockPrefix ? '0.00' : ''
  const num = parseFloat(cleaned)
  if (Number.isNaN(num)) return lockPrefix ? '0.00' : ''
  const formatted = num.toFixed(2)
  return lockPrefix ? formatted : `${PREFIX}${formatted}`
}

export function CurrencyInput({
  value,
  onChange,
  error,
  className,
  onBlur,
  maxDigitsBeforeDecimal = DEFAULT_MAX_DIGITS_BEFORE_DECIMAL,
  lockPrefix = false,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (isFocused) return
    const cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync formatted display when not focused
    setDisplayValue(formatBlurredDisplay(cleaned, lockPrefix))
  }, [value, isFocused, lockPrefix])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let cleaned = e.target.value.replace(/[^0-9.]/g, '')

    const parts = cleaned.split('.')
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('')
    }

    const splitParts = cleaned.split('.')
    if (splitParts.length === 2 && splitParts[1].length > 2) {
      cleaned = splitParts[0] + '.' + splitParts[1].slice(0, 2)
    }

    const [intPart, decPart] = cleaned.split('.')
    if ((intPart || '').length > maxDigitsBeforeDecimal) {
      cleaned =
        (intPart || '').slice(0, maxDigitsBeforeDecimal) +
        (decPart != null ? '.' + decPart.slice(0, 2) : '')
    }

    const nextDisplay = formatFocusedDisplay(cleaned, lockPrefix)
    onChange(cleaned)
    setDisplayValue(nextDisplay)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    const cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
    setDisplayValue(formatFocusedDisplay(cleaned, lockPrefix))
    if (props.onFocus) {
      props.onFocus(e)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    const cleaned = String(value ?? '').replace(/[^0-9.]/g, '')
    setDisplayValue(formatBlurredDisplay(cleaned, lockPrefix))
    if (onBlur) {
      onBlur(e)
    }
  }

  const input = (
    <input
      {...props}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(
        'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm transition-all',
        'focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/25 focus:border-[rgb(21_151_255)]',
        'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70',
        className,
        lockPrefix && 'pl-7',
        error
          ? '!border-red-500 !border-solid focus:!border-red-500 focus:!ring-2 focus:!ring-red-500'
          : '',
      )}
    />
  )

  return (
    <>
      {lockPrefix ? (
        <div className="relative w-full">
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-700',
              props.disabled && 'text-slate-400',
            )}
          >
            $
          </span>
          {input}
        </div>
      ) : (
        input
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </>
  )
}
